import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, ReactNode } from "react";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

interface Props {
  // extra class on the scroll track, e.g. "v2-offers__track"
  trackClassName?: string;
  // pixels to scroll per arrow click (roughly one card width + gap)
  step?: number;
  children: ReactNode;
}

// Fixed-width card row in a native scroll-snap track. Native overflow scrolling
// clamps cleanly at both ends — no trailing empty space, unlike react-slick's
// variableWidth (whose track/paging math is derived from slidesToShow and leaves
// a gap at the end). Arrows scroll by ~one card and hide at the start/end.
export default function ScrollCarousel({ trackClassName = "", step = 360, children }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [dragging, setDragging] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);

  // Measure on mount and after async content swaps in. Without this, a row that starts
  // with non-overflowing skeletons can keep the next arrow hidden until scroll.
  useLayoutEffect(() => {
    update();
    const frame = window.requestAnimationFrame(update);

    return () => window.cancelAnimationFrame(frame);
  }, [children, update]);

  useEffect(() => {
    const el = trackRef.current;
    el?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollByStep = (dir: number) => {
    trackRef.current?.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const dragStateRef = useRef({ startX: 0, startScrollLeft: 0, moved: false });

  const onTrackMouseDown = (e: React.MouseEvent) => {
    const el = trackRef.current;
    if (!el || e.button !== 0) return;
    e.preventDefault();
    dragStateRef.current = { startX: e.pageX, startScrollLeft: el.scrollLeft, moved: false };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const el = trackRef.current;
    if (!el) return;

    const onMouseMove = (e: MouseEvent) => {
      const dx = e.pageX - dragStateRef.current.startX;
      if (Math.abs(dx) > 3) dragStateRef.current.moved = true;
      el.scrollLeft = dragStateRef.current.startScrollLeft - dx;
    };

    const onMouseUp = () => setDragging(false);

    const onClickCapture = (e: MouseEvent) => {
      if (dragStateRef.current.moved) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    el.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("click", onClickCapture, true);
    };
  }, [dragging]);

  return (
    <div className={`v2-carousel ${atStart ? "is-at-start" : ""} ${atEnd ? "is-at-end" : ""}`.trim()}>
      <button
        type="button"
        className={`v2-carousel-arrow v2-carousel-arrow--prev ${atStart ? "is-hidden" : ""}`}
        onClick={() => scrollByStep(-1)}
        aria-label="Previous"
      >
        <Icon path={mdiChevronLeft} size="24px" />
      </button>

      <div
        className={`v2-carousel__track ${trackClassName} ${dragging ? "is-dragging" : ""}`.trim()}
        ref={trackRef}
        onMouseDown={onTrackMouseDown}
      >
        {children}
      </div>

      <button
        type="button"
        className={`v2-carousel-arrow v2-carousel-arrow--next ${atEnd ? "is-hidden" : ""}`}
        onClick={() => scrollByStep(1)}
        aria-label="Next"
      >
        <Icon path={mdiChevronRight} size="24px" />
      </button>
    </div>
  );
}
